import { useEffect, useState, useCallback } from "react";
import { useNavigate, Link } from "react-router-dom";
import { useAuth } from "../contexts/AuthContext";
import { api, fmtMoney } from "../lib/api";
import PageHeader from "../components/PageHeader";
import { Badge } from "../components/ui/badge";
import { Button } from "../components/ui/button";
import { toast } from "sonner";
import {
    LineChart, Line, XAxis, YAxis, Tooltip, ResponsiveContainer, CartesianGrid,
} from "recharts";
import {
    ShieldAlert, ShieldCheck, ShieldOff, TrendingUp, AlertTriangle,
    RefreshCw, Clock, CheckCircle2, ArrowRight, Zap, Activity, Link2,
} from "lucide-react";

const BAND_COLOR = {
    healthy:  { bg: "bg-emerald-50",  border: "border-emerald-200",  text: "text-emerald-700",  dot: "bg-emerald-500"  },
    at_risk:  { bg: "bg-amber-50",    border: "border-amber-200",    text: "text-amber-700",    dot: "bg-amber-500"    },
    critical: { bg: "bg-red-50",      border: "border-red-200",      text: "text-red-700",      dot: "bg-red-500"      },
};

export default function ManagingPartner() {
    const { user } = useAuth();
    const nav = useNavigate();

    const isAdmin = user?.role === "admin" || user?.role === "super_admin";

    useEffect(() => {
        if (!isAdmin) nav("/dashboard", { replace: true });
    }, [isAdmin, nav]);

    const [health, setHealth]       = useState(null);
    const [history, setHistory]     = useState([]);
    const [pipeline, setPipeline]   = useState([]);
    const [leakage, setLeakage]     = useState(null);
    const [briefing, setBriefing]   = useState(null);
    const [digest, setDigest]       = useState(null);
    const [brokerPerf, setBrokerPerf] = useState(null);
    const [atRiskDeals, setAtRiskDeals] = useState([]);
    const [loadingHealth, setLoadingHealth] = useState(true);
    const [loadingBriefing, setLoadingBriefing] = useState(true);
    const [loadingDigest, setLoadingDigest] = useState(true);
    const [loadingBrokerPerf, setLoadingBrokerPerf] = useState(true);
    const [refreshing, setRefreshing] = useState(false);
    const [refreshingDigest, setRefreshingDigest] = useState(false);
    const [refreshingBrokerPerf, setRefreshingBrokerPerf] = useState(false);

    const loadHealth = useCallback(async () => {
        setLoadingHealth(true);
        setLoadingBrokerPerf(true);
        try {
            const [hRes, histRes, pRes, lRes, bpRes] = await Promise.all([
                api.get("/intelligence/firm-health"),
                api.get("/intelligence/firm-health/history"),
                api.get("/deals/health-summary"),
                api.get("/intelligence/leakage/summary").catch(() => ({ data: null })),
                api.get("/intelligence/broker-performance").catch(() => ({ data: null })),
            ]);
            setHealth(hRes.data);
            setHistory(histRes.data.slice(0, 30).reverse());
            const pipelineData = pRes.data || [];
            setPipeline(pipelineData);
            setLeakage(lRes.data);
            setBrokerPerf(bpRes.data);
            const highRisk = pipelineData.filter(d => d.risk_band === "High" || d.risk_band === "Medium");
            setAtRiskDeals(highRisk);
        } catch (e) {
            toast.error("Failed to load firm health data");
        } finally {
            setLoadingHealth(false);
            setLoadingBrokerPerf(false);
        }
    }, []);

    const loadBriefing = useCallback(async (forceRefresh = false) => {
        setLoadingBriefing(true);
        try {
            const url = forceRefresh ? "/intelligence/briefing?refresh=true" : "/intelligence/briefing";
            const res = await api.get(url);
            setBriefing(res.data);
        } catch (e) {
            if (e.response?.status === 503) {
                setBriefing({ _error: "AI features not configured — add ANTHROPIC_API_KEY to backend/.env" });
            } else {
                setBriefing({ _error: e.response?.data?.detail || "Briefing unavailable" });
            }
        } finally {
            setLoadingBriefing(false);
        }
    }, []);

    const loadDigest = useCallback(async (forceRefresh = false) => {
        setLoadingDigest(true);
        try {
            const url = forceRefresh
                ? "/intelligence/admin-digest?refresh=true"
                : "/intelligence/admin-digest";
            const res = await api.get(url);
            setDigest(res.data);
        } catch (e) {
            setDigest({ _error: e.response?.data?.detail || "Digest unavailable" });
        } finally {
            setLoadingDigest(false);
        }
    }, []);

    const handleRefresh = async () => {
        setRefreshing(true);
        await loadBriefing(true);
        setRefreshing(false);
        toast.success("Briefing refreshed");
    };

    const handleRefreshDigest = async () => {
        setRefreshingDigest(true);
        await loadDigest(true);
        setRefreshingDigest(false);
        toast.success("Digest refreshed");
    };

    const handleRefreshBrokerPerf = async () => {
        setRefreshingBrokerPerf(true);
        setLoadingBrokerPerf(true);
        try {
            const res = await api.get("/intelligence/broker-performance?refresh=true");
            setBrokerPerf(res.data);
            toast.success("Broker performance refreshed");
        } catch {
            toast.error("Failed to refresh broker performance");
        } finally {
            setRefreshingBrokerPerf(false);
            setLoadingBrokerPerf(false);
        }
    };

    useEffect(() => {
        if (!isAdmin) return;
        loadHealth();
        loadBriefing();
        loadDigest();
    }, [isAdmin, loadHealth, loadBriefing, loadDigest]);

    if (!isAdmin) return null;

    const bandColors = health ? (BAND_COLOR[health.band] || BAND_COLOR.at_risk) : {};
    const sparkData  = history.slice(-7).map((h, i) => ({ i, score: h.firm_health_score }));

    const critical = pipeline.filter(d => d.health_band === "critical").length;
    const atRisk   = pipeline.filter(d => d.health_band === "at_risk").length;
    const healthy  = pipeline.filter(d => d.health_band === "healthy").length;

    return (
        <div data-testid="managing-partner-page">
            <PageHeader title="Managing Partner" subtitle="Firm health, intelligence briefing and pipeline overview." />

            <div className="p-6 lg:p-8 space-y-8">

                {/* ── Firm Health Score ────────────────────────────────── */}
                {loadingHealth ? (
                    <div className="text-sm text-muted-foreground">Computing firm health…</div>
                ) : health && (
                    <div className={`border rounded-lg p-6 ${bandColors.border} ${bandColors.bg}`}
                         data-testid="firm-health-card">
                        <div className="flex flex-col lg:flex-row lg:items-center gap-6">
                            {/* Score */}
                            <div className="flex-shrink-0">
                                <div className={`text-7xl font-bold font-mono ${bandColors.text}`}
                                     data-testid="firm-health-score">
                                    {health.firm_health_score}
                                </div>
                                <div className="text-sm text-muted-foreground mt-1">out of 100</div>
                                <Badge
                                    className={`mt-2 uppercase tracking-wider text-xs ${bandColors.bg} ${bandColors.text} border ${bandColors.border}`}
                                    data-testid="firm-health-band"
                                >
                                    <span className={`w-1.5 h-1.5 rounded-full ${bandColors.dot} mr-1.5 inline-block`} />
                                    {health.band.replace("_", " ")}
                                </Badge>
                            </div>

                            {/* Dimensions table */}
                            <div className="flex-1 overflow-x-auto">
                                <table className="w-full text-sm" data-testid="dimensions-table">
                                    <thead>
                                        <tr className="text-left text-xs uppercase tracking-wider text-muted-foreground border-b border-border/50">
                                            <th className="pb-2 font-medium">Dimension</th>
                                            <th className="pb-2 font-medium text-right w-20">Score</th>
                                            <th className="pb-2 pl-4">Detail</th>
                                        </tr>
                                    </thead>
                                    <tbody className="divide-y divide-border/40">
                                        {health.dimensions.map(dim => {
                                            const pct = dim.max_score ? dim.score / dim.max_score : 0;
                                            const barColor = pct >= 0.7 ? "bg-emerald-400" : pct >= 0.4 ? "bg-amber-400" : "bg-red-400";
                                            return (
                                                <tr key={dim.name} data-testid={`dim-row-${dim.name}`}>
                                                    <td className="py-2 pr-4 font-medium capitalize">
                                                        {dim.name.replace(/_/g, " ")}
                                                    </td>
                                                    <td className="py-2 text-right font-mono">
                                                        <div className="flex items-center justify-end gap-2">
                                                            <div className="w-16 h-1.5 rounded-full bg-border/60 overflow-hidden">
                                                                <div
                                                                    className={`h-full rounded-full ${barColor}`}
                                                                    style={{ width: `${pct * 100}%` }}
                                                                />
                                                            </div>
                                                            <span>{dim.score}<span className="text-muted-foreground text-xs">/{dim.max_score}</span></span>
                                                        </div>
                                                    </td>
                                                    <td className="py-2 pl-4 text-muted-foreground text-xs">{dim.detail}</td>
                                                </tr>
                                            );
                                        })}
                                    </tbody>
                                </table>
                            </div>

                            {/* Sparkline (last 7 readings) */}
                            {sparkData.length >= 2 && (
                                <div className="flex-shrink-0 w-48 hidden lg:block">
                                    <div className="text-[10px] uppercase tracking-wider text-muted-foreground mb-1">Last {sparkData.length} readings</div>
                                    <div className="h-20">
                                        <ResponsiveContainer width="100%" height="100%">
                                            <LineChart data={sparkData}>
                                                <Line type="monotone" dataKey="score" stroke={health.band === "healthy" ? "#10b981" : health.band === "at_risk" ? "#f59e0b" : "#ef4444"} strokeWidth={2} dot={false} />
                                                <YAxis domain={[0, 100]} hide />
                                                <Tooltip
                                                    contentStyle={{ fontSize: 11, border: "1px solid #E5E5E5", borderRadius: 4 }}
                                                    formatter={(v) => [`${v}/100`, "Score"]}
                                                    labelFormatter={() => ""}
                                                />
                                            </LineChart>
                                        </ResponsiveContainer>
                                    </div>
                                </div>
                            )}
                        </div>
                    </div>
                )}

                {/* ── 30-day Trend ──────────────────────────────────────── */}
                {history.length >= 2 && (
                    <div className="border border-border rounded-md bg-white p-6" data-testid="trend-chart">
                        <h2 className="font-display text-xl font-bold tracking-tight mb-4">30-day Trend</h2>
                        <div className="h-52">
                            <ResponsiveContainer width="100%" height="100%">
                                <LineChart data={history.map((h, i) => ({ i: i + 1, score: h.firm_health_score, band: h.band }))}>
                                    <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
                                    <XAxis dataKey="i" tick={{ fontSize: 10, fill: "#737373" }} axisLine={false} tickLine={false} label={{ value: "Check", position: "insideBottomRight", offset: -5, fontSize: 10, fill: "#737373" }} />
                                    <YAxis domain={[0, 100]} tick={{ fontSize: 10, fill: "#737373" }} axisLine={false} tickLine={false} />
                                    <Tooltip
                                        contentStyle={{ fontSize: 11, border: "1px solid #E5E5E5", borderRadius: 4 }}
                                        formatter={(v) => [`${v}/100`, "Score"]}
                                    />
                                    <Line type="monotone" dataKey="score" stroke="hsl(218 72% 14%)" strokeWidth={2} dot={{ r: 2, fill: "hsl(218 72% 14%)" }} />
                                </LineChart>
                            </ResponsiveContainer>
                        </div>
                    </div>
                )}

                {/* ── Pipeline Snapshot ─────────────────────────────────── */}
                <div className="border border-border rounded-md bg-white p-6" data-testid="pipeline-snapshot">
                    <div className="flex items-center justify-between mb-4">
                        <h2 className="font-display text-xl font-bold tracking-tight">Pipeline Snapshot</h2>
                        <Link to="/pipeline" className="text-xs uppercase tracking-wider text-muted-foreground hover:text-foreground">
                            View pipeline →
                        </Link>
                    </div>
                    <div className="grid grid-cols-3 gap-4">
                        <div className="flex items-center gap-3 p-4 rounded-md bg-red-50 border border-red-100" data-testid="mp-critical-count">
                            <ShieldAlert className="w-5 h-5 text-red-500" />
                            <div>
                                <div className="text-2xl font-bold font-mono text-red-600">{critical}</div>
                                <div className="text-xs text-red-500 uppercase tracking-wider">Critical</div>
                            </div>
                        </div>
                        <div className="flex items-center gap-3 p-4 rounded-md bg-amber-50 border border-amber-100" data-testid="mp-at-risk-count">
                            <ShieldOff className="w-5 h-5 text-amber-500" />
                            <div>
                                <div className="text-2xl font-bold font-mono text-amber-600">{atRisk}</div>
                                <div className="text-xs text-amber-600 uppercase tracking-wider">At Risk</div>
                            </div>
                        </div>
                        <div className="flex items-center gap-3 p-4 rounded-md bg-green-50 border border-green-100" data-testid="mp-healthy-count">
                            <ShieldCheck className="w-5 h-5 text-green-500" />
                            <div>
                                <div className="text-2xl font-bold font-mono text-green-600">{healthy}</div>
                                <div className="text-xs text-green-600 uppercase tracking-wider">Healthy</div>
                            </div>
                        </div>
                    </div>
                </div>

                {/* ── Deals at Risk (Deal Risk Score) ──────────────────── */}
                {atRiskDeals.length > 0 && (
                    <div className="border border-border rounded-md bg-white p-6" data-testid="mp-deals-at-risk">
                        <div className="flex items-center justify-between mb-4">
                            <h2 className="font-display text-xl font-bold tracking-tight flex items-center gap-2">
                                <AlertTriangle className="w-4 h-4 text-amber-500" /> Deals at Risk
                                <span className="ml-2 inline-flex items-center justify-center w-6 h-6 rounded-full bg-red-100 text-red-700 text-xs font-bold" data-testid="mp-risk-count">
                                    {atRiskDeals.length}
                                </span>
                            </h2>
                            <Link to="/pipeline" className="text-xs uppercase tracking-wider text-muted-foreground hover:text-foreground">
                                View pipeline →
                            </Link>
                        </div>
                        <div className="divide-y divide-border">
                            {atRiskDeals.slice(0, 8).map(d => {
                                const isHigh = d.risk_band === "High";
                                return (
                                    <div key={d.id} className="py-2.5 flex items-center justify-between gap-3" data-testid={`mp-risk-deal-${d.id}`}>
                                        <div className="flex items-center gap-2 min-w-0">
                                            <span className={`inline-block w-2 h-2 rounded-full flex-shrink-0 ${isHigh ? "bg-red-500" : "bg-amber-400"}`} />
                                            <Link to={`/deals/${d.id}`} className="text-sm font-medium hover:underline truncate">{d.company_name}</Link>
                                            <span className="text-xs text-muted-foreground hidden sm:inline">{d.stage}</span>
                                        </div>
                                        <div className="flex items-center gap-2 flex-shrink-0">
                                            <span className={`text-[10px] font-semibold uppercase px-2 py-0.5 rounded-sm border ${isHigh ? "bg-red-50 text-red-700 border-red-200" : "bg-amber-50 text-amber-700 border-amber-200"}`}>
                                                {d.risk_band}
                                            </span>
                                            <span className="font-mono text-xs text-muted-foreground">{d.risk_score}</span>
                                        </div>
                                    </div>
                                );
                            })}
                        </div>
                        {atRiskDeals.length > 8 && (
                            <Link to="/pipeline" className="mt-3 block text-xs text-muted-foreground hover:text-foreground">
                                +{atRiskDeals.length - 8} more — view all in pipeline
                            </Link>
                        )}
                    </div>
                )}

                {/* ── Revenue at Risk ───────────────────────────────────── */}
                {leakage && (
                    <div className="border border-border rounded-md bg-white p-6 cursor-pointer hover:shadow-md transition-shadow"
                         onClick={() => nav("/intelligence/leakage")}
                         data-testid="mp-leakage-card">
                        <div className="flex items-center justify-between mb-1">
                            <h2 className="font-display text-xl font-bold tracking-tight">Revenue at Risk</h2>
                            <span className="text-xs uppercase tracking-wider text-muted-foreground">View details →</span>
                        </div>
                        <div className="text-3xl font-bold font-mono text-red-600 mb-2" data-testid="mp-total-at-risk">
                            {leakage.total_fee_at_risk > 0
                                ? `£${Number(leakage.total_fee_at_risk).toLocaleString("en-GB", { maximumFractionDigits: 0 })}`
                                : "£0"}
                        </div>
                        <p className="text-xs text-muted-foreground">
                            {leakage.count_dormant} dormant · {leakage.count_stale_buyers} stale follow-ups · {leakage.count_uncontacted_matches} uncontacted matches
                        </p>
                    </div>
                )}

                {/* ── AI Administrator Digest ───────────────────────────── */}
                <div className="border border-border rounded-md bg-white p-6" data-testid="ai-admin-digest-section">
                    <div className="flex items-center justify-between mb-4">
                        <div>
                            <p className="text-[11px] uppercase tracking-[0.12em] font-semibold text-[#C9A227]">
                                Data Quality &amp; Staleness
                            </p>
                            <h2 className="font-display text-xl font-bold tracking-tight flex items-center gap-2">
                                <Activity className="w-4 h-4" /> AI Administrator
                            </h2>
                            {digest?.generated_at && !digest._error && (
                                <p className="text-xs text-muted-foreground mt-0.5 flex items-center gap-1">
                                    <Clock className="w-3 h-3" />
                                    Generated {new Date(digest.generated_at).toLocaleString("en-GB", { dateStyle: "short", timeStyle: "short" })}
                                </p>
                            )}
                        </div>
                        <Button
                            variant="outline"
                            size="sm"
                            onClick={handleRefreshDigest}
                            disabled={refreshingDigest || loadingDigest}
                            data-testid="digest-refresh-btn"
                            className="gap-1.5"
                        >
                            <RefreshCw className={`w-3.5 h-3.5 ${refreshingDigest ? "animate-spin" : ""}`} />
                            {refreshingDigest ? "Scanning…" : "Refresh"}
                        </Button>
                    </div>

                    {loadingDigest ? (
                        <div className="text-sm text-muted-foreground py-4">Scanning pipeline for issues…</div>
                    ) : digest?._error ? (
                        <div className="text-sm text-amber-600 bg-amber-50 border border-amber-200 rounded-md p-4"
                             data-testid="digest-error">
                            <AlertTriangle className="w-4 h-4 inline mr-2" />
                            {digest._error}
                        </div>
                    ) : digest ? (
                        <AdminDigestBody digest={digest} />
                    ) : null}
                </div>

                {/* ── Daily Briefing ────────────────────────────────────── */}                <div className="border border-border rounded-md bg-white p-6" data-testid="daily-briefing-section">
                    <div className="flex items-center justify-between mb-4">
                        <div>
                            <h2 className="font-display text-xl font-bold tracking-tight flex items-center gap-2">
                                <Zap className="w-4 h-4 text-gold" /> Daily AI Briefing
                            </h2>
                            {briefing?.computed_at && !briefing._error && (
                                <p className="text-xs text-muted-foreground mt-0.5 flex items-center gap-1">
                                    <Clock className="w-3 h-3" />
                                    Generated {new Date(briefing.computed_at).toLocaleString("en-GB", { dateStyle: "short", timeStyle: "short" })}
                                </p>
                            )}
                        </div>
                        <Button
                            variant="outline"
                            size="sm"
                            onClick={handleRefresh}
                            disabled={refreshing || loadingBriefing}
                            data-testid="briefing-refresh-btn"
                            className="gap-1.5"
                        >
                            <RefreshCw className={`w-3.5 h-3.5 ${refreshing ? "animate-spin" : ""}`} />
                            {refreshing ? "Refreshing…" : "Refresh"}
                        </Button>
                    </div>

                    {loadingBriefing ? (
                        <div className="text-sm text-muted-foreground py-4">Assembling briefing…</div>
                    ) : briefing?._error ? (
                        <div className="text-sm text-amber-600 bg-amber-50 border border-amber-200 rounded-md p-4" data-testid="briefing-error">
                            <AlertTriangle className="w-4 h-4 inline mr-2" />
                            {briefing._error}
                        </div>
                    ) : briefing ? (
                        <div className="space-y-6">
                            {/* Summary */}
                            <div className="text-base font-medium leading-relaxed p-4 rounded-md bg-muted/40 border border-border/60"
                                 data-testid="briefing-summary">
                                {briefing.summary}
                            </div>

                            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                                {/* Key Risks */}
                                <div>
                                    <h3 className="text-xs uppercase tracking-wider font-semibold text-muted-foreground mb-3">Key Risks</h3>
                                    <div className="space-y-2" data-testid="briefing-risks">
                                        {(briefing.key_risks || []).map((risk, i) => {
                                            const urgencyStyle = risk.urgency === "high"
                                                ? "bg-red-50 border-red-200"
                                                : "bg-amber-50 border-amber-200";
                                            const textColor = risk.urgency === "high" ? "text-red-700" : "text-amber-700";
                                            return (
                                                <div key={i}
                                                     className={`p-3 rounded-md border ${urgencyStyle}`}
                                                     data-testid={`briefing-risk-${i}`}>
                                                    <div className={`font-medium text-sm ${textColor} flex items-center gap-2`}>
                                                        <AlertTriangle className="w-3.5 h-3.5 flex-shrink-0" />
                                                        {risk.title}
                                                        <Badge variant="outline" className={`ml-auto text-[9px] uppercase tracking-wider ${textColor} border-current`}>
                                                            {risk.urgency}
                                                        </Badge>
                                                    </div>
                                                    <p className="text-xs text-muted-foreground mt-1">{risk.detail}</p>
                                                </div>
                                            );
                                        })}
                                    </div>
                                </div>

                                {/* Recommended Actions */}
                                <div>
                                    <h3 className="text-xs uppercase tracking-wider font-semibold text-muted-foreground mb-3">Recommended Actions</h3>
                                    <div className="space-y-2" data-testid="briefing-actions">
                                        {(briefing.recommended_actions || []).map((action, i) => (
                                            <div key={i}
                                                 className="p-3 rounded-md border border-border/60 bg-muted/20"
                                                 data-testid={`briefing-action-${i}`}>
                                                <div className="flex items-start gap-3">
                                                    <div className="w-5 h-5 rounded-full bg-foreground text-background text-[10px] font-bold flex items-center justify-center flex-shrink-0 mt-0.5">
                                                        {action.priority}
                                                    </div>
                                                    <div>
                                                        <div className="font-medium text-sm">{action.action}</div>
                                                        <p className="text-xs text-muted-foreground mt-0.5 flex items-center gap-1">
                                                            <CheckCircle2 className="w-3 h-3 text-emerald-500" />
                                                            {action.expected_impact}
                                                        </p>
                                                    </div>
                                                </div>
                                            </div>
                                        ))}
                                    </div>
                                </div>
                            </div>

                            {/* 90-day outlook */}
                            {briefing.outlook_90d && (
                                <div className="pt-4 border-t border-border/60">
                                    <h3 className="text-xs uppercase tracking-wider font-semibold text-muted-foreground mb-2">90-Day Outlook</h3>
                                    <p className="text-sm flex items-center gap-2" data-testid="briefing-outlook">
                                        <TrendingUp className="w-4 h-4 text-emerald-500 flex-shrink-0" />
                                        {briefing.outlook_90d}
                                    </p>
                                </div>
                            )}
                        </div>
                    ) : null}
                </div>

                {/* ── Broker Performance ────────────────────────────────── */}
                <div className="border border-border rounded-md bg-white p-6"
                     data-testid="broker-performance-card">
                    <div className="flex items-center justify-between mb-4">
                        <div>
                            <p className="text-[11px] uppercase tracking-[0.12em] font-semibold text-[#C9A227]">
                                People Performance
                            </p>
                            <h2 className="font-display text-xl font-bold tracking-tight flex items-center gap-2">
                                <TrendingUp className="w-4 h-4" /> Broker Performance
                            </h2>
                            {brokerPerf?.generated_at && (
                                <p className="text-xs text-muted-foreground mt-0.5 flex items-center gap-1">
                                    <Clock className="w-3 h-3" />
                                    Updated {new Date(brokerPerf.generated_at).toLocaleString("en-GB", { dateStyle: "short", timeStyle: "short" })}
                                </p>
                            )}
                        </div>
                        <Button
                            variant="outline"
                            size="sm"
                            onClick={handleRefreshBrokerPerf}
                            disabled={refreshingBrokerPerf || loadingBrokerPerf}
                            data-testid="broker-perf-refresh-btn"
                            className="gap-1.5"
                        >
                            <RefreshCw className={`w-3.5 h-3.5 ${refreshingBrokerPerf ? "animate-spin" : ""}`} />
                            {refreshingBrokerPerf ? "Refreshing…" : "Refresh"}
                        </Button>
                    </div>
                    {loadingBrokerPerf ? (
                        <div className="text-sm text-muted-foreground py-4">Loading broker metrics…</div>
                    ) : (
                        <BrokerPerformanceSection data={brokerPerf} />
                    )}
                </div>

                {/* ── Daily Briefing ───────────────────────────────────── */}            </div>
        </div>
    );
}


// ─────────────────────────────────────────────────────────────────────────────
// AI Administrator Digest Body
// ─────────────────────────────────────────────────────────────────────────────

const CATEGORY_LABEL = {
    missing_data:      "Missing Data",
    stalled_deals:     "Stalled Deals",
    contact_dormancy:  "Contact Dormancy",
    readiness_gap:     "Readiness Gap",
};

const SEV_STYLE = {
    high:   { dot: "bg-red-500",    badge: "bg-red-50 text-red-700 border-red-200"   },
    medium: { dot: "bg-amber-400",  badge: "bg-amber-50 text-amber-700 border-amber-200" },
    low:    { dot: "bg-slate-300",  badge: "bg-slate-50 text-slate-600 border-slate-200" },
};

function SeverityPill({ severity, count }) {
    if (!count) return null;
    const s = SEV_STYLE[severity] || SEV_STYLE.low;
    return (
        <span className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-medium border ${s.badge}`}
              data-testid={`digest-pill-${severity}`}>
            <span className={`w-1.5 h-1.5 rounded-full ${s.dot}`} />
            {count} {severity}
        </span>
    );
}

function AdminDigestBody({ digest }) {
    const { items = [], ai_narrative, summary_counts = {} } = digest;

    if (items.length === 0) {
        return (
            <div className="text-sm text-emerald-700 bg-emerald-50 border border-emerald-200 rounded-md p-4 flex items-center gap-2"
                 data-testid="digest-empty-state">
                <CheckCircle2 className="w-4 h-4 flex-shrink-0" />
                No data quality or staleness issues found — pipeline is clean.
            </div>
        );
    }

    // Group items by category preserving insertion order
    const groups = {};
    for (const item of items) {
        (groups[item.category] = groups[item.category] || []).push(item);
    }

    return (
        <div className="space-y-5" data-testid="digest-body">
            {/* Summary pills */}
            <div className="flex flex-wrap gap-2" data-testid="digest-summary-pills">
                <SeverityPill severity="high"   count={summary_counts.high}   />
                <SeverityPill severity="medium" count={summary_counts.medium} />
                <SeverityPill severity="low"    count={summary_counts.low}    />
                <span className="inline-flex items-center text-xs text-muted-foreground pl-1">
                    {summary_counts.total} total issue{summary_counts.total !== 1 ? "s" : ""}
                </span>
            </div>

            {/* AI narrative */}
            {ai_narrative && (
                <div className="text-sm leading-relaxed p-4 rounded-md bg-muted/40 border border-border/60"
                     data-testid="digest-narrative">
                    {ai_narrative}
                </div>
            )}

            {/* Items grouped by category */}
            {Object.entries(groups).map(([cat, catItems]) => (
                <div key={cat} data-testid={`digest-category-${cat}`}>
                    <h3 className="text-[11px] uppercase tracking-wider font-semibold text-muted-foreground mb-2">
                        {CATEGORY_LABEL[cat] || cat}
                    </h3>
                    <div className="divide-y divide-border/40 border border-border/60 rounded-md overflow-hidden">
                        {catItems.map((item, idx) => {
                            const s = SEV_STYLE[item.severity] || SEV_STYLE.low;
                            return (
                                <div key={idx}
                                     className="flex items-start gap-3 px-3 py-2.5 hover:bg-accent/20 transition-colors"
                                     data-testid={`digest-item-${cat}-${idx}`}>
                                    <span className={`mt-1.5 w-2 h-2 rounded-full flex-shrink-0 ${s.dot}`} />
                                    <div className="min-w-0 flex-1">
                                        <a href={item.link_to_record}
                                           className="text-sm font-medium hover:underline"
                                           onClick={e => { e.preventDefault(); window.location.href = item.link_to_record; }}>
                                            {item.item}
                                        </a>
                                        <p className="text-xs text-muted-foreground mt-0.5 leading-snug">
                                            {item.description}
                                        </p>
                                    </div>
                                    <span className={`flex-shrink-0 text-[10px] uppercase tracking-wider px-2 py-0.5 rounded-sm border font-medium ${s.badge}`}>
                                        {item.severity}
                                    </span>
                                </div>
                            );
                        })}
                    </div>
                </div>
            ))}
        </div>
    );
}


// ─────────────────────────────────────────────────────────────────────────────
// Broker Performance Section
// ─────────────────────────────────────────────────────────────────────────────

function pct(v) { return `${Math.round((v || 0) * 100)}%`; }

function crColor(rate) {
    if (rate >= 0.6) return "text-emerald-600";
    if (rate >= 0.4) return "text-amber-600";
    return "text-red-600";
}

function hsColor(score) {
    if (score == null) return "text-muted-foreground";
    if (score >= 70) return "text-emerald-600";
    if (score >= 40) return "text-amber-600";
    return "text-red-600";
}

const SORT_KEYS = {
    name:                   (a, b) => (a.broker_name || a.name).localeCompare(b.broker_name || b.name),
    active_deals:           (a, b) => b.active_deals - a.active_deals,
    pipeline_value:         (a, b) => (b.pipeline_value || 0) - (a.pipeline_value || 0),
    completed_value:        (a, b) => (b.completed_value || 0) - (a.completed_value || 0),
    conversion_rate:        (a, b) => (b.conversion_rate || b.completion_rate || 0) - (a.conversion_rate || a.completion_rate || 0),
    fee_contribution:       (a, b) => (b.fee_contribution || b.total_fees_generated || 0) - (a.fee_contribution || a.total_fees_generated || 0),
    avg_days_to_close:      (a, b) => (b.avg_days_to_close ?? b.avg_deal_duration_days ?? -1) - (a.avg_days_to_close ?? a.avg_deal_duration_days ?? -1),
    stale_deal_count:       (a, b) => (b.stale_deal_count ?? b.stale_deals ?? 0) - (a.stale_deal_count ?? a.stale_deals ?? 0),
    avg_health_score:       (a, b) => (b.avg_health_score ?? -1) - (a.avg_health_score ?? -1),
};

function StaleDot({ count }) {
    if (count == null) return <span className="text-muted-foreground">—</span>;
    const dotCls = count >= 3 ? "bg-red-500" : count >= 1 ? "bg-amber-400" : "bg-slate-300";
    const textCls = count >= 3 ? "text-red-600" : count >= 1 ? "text-amber-600" : "text-muted-foreground";
    return (
        <span className={`inline-flex items-center gap-1.5 font-medium tabular-nums ${textCls}`}
              data-testid="bp-stale-dot">
            <span className={`w-2 h-2 rounded-full flex-shrink-0 ${dotCls}`} />
            {count}
        </span>
    );
}

function BrokerPerformanceSection({ data }) {
    const [sortKey, setSortKey] = useState("fee_contribution");

    if (!data) return null;

    const { brokers = [], team_summary = {} } = data;
    const sorted = [...brokers].sort(SORT_KEYS[sortKey] || (() => 0));

    const ColHeader = ({ k, label }) => (
        <th
            className={`px-4 py-2.5 text-left text-xs font-medium uppercase tracking-wider cursor-pointer select-none hover:text-foreground transition-colors ${
                sortKey === k ? "text-foreground" : "text-muted-foreground"
            }`}
            onClick={() => setSortKey(k)}
            data-testid={`bp-sort-${k}`}
        >
            {label}{sortKey === k && " ↓"}
        </th>
    );

    return (
        <div data-testid="broker-performance-section">
            {/* Table */}
            <div className="border border-border/60 rounded-lg overflow-hidden">
                <table className="w-full text-[13px]">
                    <thead className="bg-accent/30 border-b border-border/60">
                        <tr>
                            <ColHeader k="name"               label="Name" />
                            <ColHeader k="active_deals"       label="Active" />
                            <ColHeader k="pipeline_value"     label="Pipeline" />
                            <ColHeader k="completed_value"    label="Closed Value (12m)" />
                            <ColHeader k="conversion_rate"    label="Conv. Rate" />
                            <ColHeader k="fee_contribution"   label="Fees" />
                            <ColHeader k="avg_days_to_close"  label="Avg Days" />
                            <ColHeader k="stale_deal_count"   label="Stale" />
                        </tr>
                    </thead>
                    <tbody className="divide-y divide-border/40">
                        {sorted.map((b) => {
                            const id = b.broker_id || b.user_id;
                            const name = b.broker_name || b.name;
                            const cv = b.completed_value ?? 0;
                            const fees = b.fee_contribution ?? b.total_fees_generated ?? 0;
                            const cr = b.conversion_rate ?? b.completion_rate ?? 0;
                            const avgDays = b.avg_days_to_close ?? b.avg_deal_duration_days;
                            const stale = b.stale_deal_count ?? b.stale_deals ?? 0;
                            const pv = b.pipeline_value ?? 0;
                            return (
                                <tr key={id} className="h-11 hover:bg-accent/20 transition-colors"
                                    data-testid={`bp-row-${id}`}>
                                    <td className="px-4 py-2.5">
                                        <span className="font-medium">{name}</span>
                                        <span className="ml-2 text-[11px] text-muted-foreground capitalize">{b.role}</span>
                                    </td>
                                    <td className="px-4 py-2.5 tabular-nums">{b.active_deals}</td>
                                    <td className="px-4 py-2.5 tabular-nums text-muted-foreground">
                                        {pv > 0 ? `£${pv.toLocaleString()}` : "—"}
                                    </td>
                                    <td className="px-4 py-2.5 font-medium tabular-nums text-[#C9A227]">
                                        {cv > 0 ? `£${cv.toLocaleString()}` : "—"}
                                    </td>
                                    <td className={`px-4 py-2.5 font-medium tabular-nums ${crColor(cr)}`}>
                                        {pct(cr)}
                                    </td>
                                    <td className="px-4 py-2.5 font-medium tabular-nums text-[#C9A227]">
                                        {fees > 0 ? `£${fees.toLocaleString()}` : "—"}
                                    </td>
                                    <td className="px-4 py-2.5 text-muted-foreground tabular-nums">
                                        {avgDays != null ? `${avgDays}d` : "—"}
                                    </td>
                                    <td className="px-4 py-2.5">
                                        <StaleDot count={stale} />
                                    </td>
                                </tr>
                            );
                        })}
                            <tr><td colSpan={7} className="px-4 py-6 text-center text-sm text-muted-foreground">No broker data yet</td></tr>
                        )}
                    </tbody>
                </table>
            </div>

            {/* Team summary strip */}
            <div className="mt-4 grid grid-cols-3 gap-3" data-testid="bp-team-summary">
                <div className="border border-border/60 rounded-md p-3.5 space-y-0.5">
                    <p className="text-[11px] uppercase tracking-wider text-muted-foreground">Team Completion Rate</p>
                    <p className={`text-lg font-semibold tabular-nums ${crColor(team_summary.team_completion_rate)}`}>
                        {pct(team_summary.team_completion_rate)}
                    </p>
                </div>
                <div className="border border-border/60 rounded-md p-3.5 space-y-0.5">
                    <p className="text-[11px] uppercase tracking-wider text-muted-foreground">Total Fees Generated</p>
                    <p className="text-lg font-semibold tabular-nums text-[#C9A227]">
                        {team_summary.total_fees_generated > 0 ? `£${team_summary.total_fees_generated.toLocaleString()}` : "—"}
                    </p>
                </div>
                <div className="border border-border/60 rounded-md p-3.5 space-y-0.5">
                    <p className="text-[11px] uppercase tracking-wider text-muted-foreground">Most Active Broker</p>
                    <p className="text-lg font-semibold truncate">{team_summary.most_active_broker || "—"}</p>
                </div>
            </div>
        </div>
    );
}
